import styles from "../../assets/scss/modules/PublicListings.module.scss";
import React, {HTMLAttributes, useEffect, useState} from 'react';
import {CtrInscriptions} from "../../graphql/generated/graphql-main.tsx";
import clsx from "clsx";
import {ClickAwayListener, IconButton, Paper, Popper, Slider} from "@mui/material";
import {useWpContext} from "../context/WordPressProvider.tsx";
import Button from "@mui/material/Button";
import {Close} from "@mui/icons-material";
import {maxBy} from "../../utils/array-utils.ts";
import {useDebounceValue} from "usehooks-ts";
import useReactTexts from "../../hooks/useReactTexts.ts";

interface Props extends HTMLAttributes<HTMLDivElement> {
    listings: CtrInscriptions[];
    onSetRange: (min: number | null, max: number | null) => void;
    resetFlag: number;
}

const FiltersPriceSlider = (props: Props) => {
    const {is_fr} = useWpContext();
    const {__} = useReactTexts();
    // try to get the highest price listed (excluding rentals) for use in slider.max
    const highestPrice: number = props.listings.reduce(maxBy("PRIX_DEMANDE"), 0);

    // use button width at moment of click to set width of popper element, so it fits visually with button
    const [btnWidth, setBtnWidth] = useState<number | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(null);
    // ranges (% & $) - % used in slider, $ used for actual filtering
    const [rngTicks, setRngTicks] = useState<number[]>([0, 100]);
    const [rngPrice, setRngPrice] = useState<number[]>([0, highestPrice]);
    // it's imperative to debounce, else if user slides frantically, it will jam everything
    const [debouncedRange] = useDebounceValue(rngPrice, 500);

    const togglePopper = (el: HTMLElement | SVGElement | null) => {
        if (anchorEl == null) setAnchorEl(el);
        else setAnchorEl(null);
    }

    // we use % for slider range, so must convert to actual price based on highest price in our pool of data
    const tick2price = (tick: number) => {
        // dumb linear way to match any price
        return (tick / 100) * highestPrice;

        // todo continuer later to implement proper non-linear slider gradation
        // divide the highest price per 100 ticks
        const perTick = highestPrice / 100;
        let b1 = 1_000_000 / 250_000; // 250k
        let b2 = (Math.min(10_000_000, highestPrice) - 1_000_000) / 500_000; // 500k
        let b3 = (highestPrice - 10_000_000) / 5_000_000; // 5m
        const sumOfWeights = b1 + b2 + b3;

        // put back on a scale of 100 (% of the slider per price range)
        b1 = (b1 * 100) / sumOfWeights;
        b2 = (b2 * 100) / sumOfWeights;
        b3 = (b3 * 100) / sumOfWeights;

        console.info(`tick (${b1}, ${b2}, ${b3})`, tick);
        if (tick <= b1)
            return tick * (b1 / 100) * perTick;
        else if (tick <= (b1 + b2))
            return tick * (b2 / 100) * perTick;
        else if (tick <= (b1 + b2 + b3))
            return tick * (b3 / 100) * perTick;
        return tick * perTick;
    }

    // value = percentage (scale of 1 - 100)
    const calculateValue = (value: number) => {
        // console.info(`scale.value`, value);
        return tick2price(value);
    }

    // properly format the range value (pre-computed via 'scale' function) in dollar (lang-specific)
    const dollarFormat = (price: number) => {
        const Dollar = new Intl.NumberFormat(is_fr ? 'fr-CA' : 'en-CA', {
            style: "currency",
            currency: 'CAD',
            maximumFractionDigits: 0
        });
        return Dollar.format(price || 0);
    }

    // handle changing of range, always stay in % while interacting with slider (convert to price in useEffect only)
    const onChangeRange = (event: Event, range: number[]) => {
        // console.info(`range.onchange`, range)
        // if (range[1] - range[0] <= minPriceDistance)
        //     range[1] = range[0] + minPriceDistance;
        // setRngTicks(range);
        setRngPrice(range);
    }

    // always keep 'prices' in sync w/ % range, converting % value to real price
    // useEffect(() => {
    //     setRngPrice([tick2price(rngTicks[0]), tick2price(rngTicks[1])]);
    // }, [rngTicks]);

    // send range to parent component w/ delay to prevent overload (debouncing); setting to null = reset
    useEffect(() => {
        // const _min = rngTicks[0] == 0 ? null : rngPrice[0];
        // const _max = rngTicks[1] == 100 ? null : rngPrice[1];
        const _min = rngPrice[0] == 0 ? null : rngPrice[0];
        const _max = rngPrice[1] == highestPrice ? null : rngPrice[1];
        props.onSetRange(_min, _max);
    }, [debouncedRange]);

    // when reset flag is updated, this triggers resetting this filter
    useEffect(() => {
        if (props.resetFlag == 0) return;
        setRngPrice([0, highestPrice]);
        // setRngTicks([0, 100]);
    }, [props.resetFlag]);

    return <div className={clsx("w-full price-filter", props.className)}>

        {/* anchor to trigger and position popper with price slider */}
        <Button
            color={"primary"} variant={"outlined"}
            className={clsx("slider-anchor", styles.price_slider_btn)}
            onClick={(e) => {
                setBtnWidth(e.currentTarget.offsetWidth);
                togglePopper(e.currentTarget);
            }}
        >
            {/* display range when it's set */}
            {/*{(rngTicks[0] == 0 && rngTicks[1] == 100)*/}
            {(rngPrice[0] == 0 && rngPrice[1] == highestPrice)
                ? __("Tous les prix")
                : <span>{dollarFormat(rngPrice[0])} - {dollarFormat(rngPrice[1])}</span>}
        </Button>

        {/* note: the ClickAwayListener is very convenient, but it may cause accidental exits if sliding fast */}
        {/* ref: 00747/code/plugin.strategies-pme/react-app/src/components/views-public/KioskPopper.tsx */}
        {Boolean(anchorEl) && <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Popper
                open={Boolean(anchorEl) || true}
                anchorEl={anchorEl}
                disablePortal={true}
                className={"z-10 w-full sm:w-1/2 xl:w-1/4"}
                // dynamic width set to match width of button
                style={{width: `${btnWidth}px`}}
                placement={"bottom-start"}
            >
                <Paper className={"py-4 px-8 mt-2 rounded-none bg-merino relative"}>
                    {/* close icon */}
                    <IconButton
                        size={"small"} className={"text-right absolute top-0 right-0"}
                        onClick={() => setAnchorEl(null)}
                    >
                        <Close fontSize={"small"}/>
                    </IconButton>
                    {/* actual slider */}
                    <Slider
                        className={"mt-4"}
                        classes={{
                            rail: "h-2",
                            track: "h-2",
                        }}
                        disableSwap={true}
                        value={rngPrice}
                        min={0}
                        max={highestPrice}
                        step={100_000}
                        // value={rngTicks}
                        // max={100}
                        // step={1}
                        valueLabelDisplay={"auto"}
                        valueLabelFormat={(value, index) => dollarFormat(value)}
                        // scale={calculateValue}
                        onChange={onChangeRange}
                    ></Slider>

                    {/* display min & max selected by user */}
                    <div className={"flex justify-between"}>
                        <div className={"slider-min"}>
                            {dollarFormat(rngPrice[0])}
                        </div>
                        <div className={"slider-max"}>
                            {dollarFormat(rngPrice[1])}
                        </div>
                    </div>
                </Paper>
            </Popper>
        </ClickAwayListener>}

    </div>
}

export default FiltersPriceSlider;
